import dayjs from 'dayjs';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import { BankAccountOption } from 'types/DropdownOption';
import { EntryType } from 'types/cash/EntryType';

export type SortDirection = 'asc' | 'desc' | null;

export interface SortState {
  key: string;
  direction: SortDirection;
  index: number; // Order of this sort (0 = primary, 1 = secondary, etc.)
}

interface CashState {
  bankAccounts: BankAccountOption[];
  setBankAccounts: (bankAccounts: BankAccountOption[]) => void;
  accountId?: string;
  setAccountId: (accountId: string) => void;
  dateRange: [Date | null, Date | null];
  setDateRange: (dateRange: [Date | null, Date | null]) => void;

  /* 
    This state tracks dirtied Select values for each individual transaction row.
    This is necessary so we know what to submit for the categorization data when approving these transactions.
    We default to the initial values for the field suggestions in the approve handler,
    so we there's no need to initialize this state with the default values.
  */
  selectedCategorizeSuggestionValues: Record<
    string,
    {
      name?: string;
      glAccount?: string;
      class?: string;
      book_memo?: string;
      entry_type?: EntryType | null;
    }
  >;
  setSelectedCategorizeSuggestionValues: (
    selectedCategorizeSuggestionValues:
      | Record<
          string,
          {
            name?: string;
            glAccount?: string;
            class?: string;
            book_memo?: string;
            entry_type?: EntryType | null;
          }
        >
      | ((
          prev: CashState['selectedCategorizeSuggestionValues']
        ) => CashState['selectedCategorizeSuggestionValues'])
  ) => void;

  // Separate sorting states for match and reconcile tabs
  matchSortStates: SortState[];
  setMatchSortStates: (sortStates: SortState[]) => void;
  reconcileSortStates: SortState[];
  setReconcileSortStates: (sortStates: SortState[]) => void;
  approvedSortStates: SortState[];
  setApprovedSortStates: (sortStates: SortState[]) => void;
}

const useCashStore = create<CashState>()(
  persist(
    (set) => ({
      accountId: undefined,
      bankAccounts: [],
      setBankAccounts: (bankAccounts) => set({ bankAccounts }),
      dateRange: [
        // View current month
        dayjs().startOf('month').toDate(),
        dayjs().endOf('month').toDate(),
      ],
      selectedCategorizeSuggestionValues: {},
      matchSortStates: [],
      reconcileSortStates: [],
      approvedSortStates: [],
      setAccountId: (accountId) => set({ accountId }),
      setDateRange: (dateRange) => set({ dateRange }),
      setSelectedCategorizeSuggestionValues: (
        selectedCategorizeSuggestionValues
      ) =>
        set((state) => ({
          selectedCategorizeSuggestionValues:
            typeof selectedCategorizeSuggestionValues === 'function'
              ? (
                  selectedCategorizeSuggestionValues as (
                    prev: CashState['selectedCategorizeSuggestionValues']
                  ) => CashState['selectedCategorizeSuggestionValues']
                )(state.selectedCategorizeSuggestionValues)
              : selectedCategorizeSuggestionValues,
        })),
      setMatchSortStates: (matchSortStates) => set({ matchSortStates }),
      setReconcileSortStates: (reconcileSortStates) =>
        set({ reconcileSortStates }),
      setApprovedSortStates: (approvedSortStates) =>
        set({ approvedSortStates }),
    }),
    {
      name: 'Cash-storage',
      storage: createJSONStorage(() => localStorage),
    }
  )
);

export { useCashStore };
